// LoanProductCardCombined.tsx
// Assuming the data includes the combined standard_low_cash and standard_low_interest
// Used exclusively for the Standard Capital Product for now

import React, { useEffect, useState } from "react"
import { Body, H2, H5, H6, Tiny } from "@/components/standard/BFText"
import { type LoanApplicationType, type ProductType } from "@/generated/graphql"
import { Platform, View } from "react-native"
import GoldTrophy from "@/assets/clipart/gold-standard-trophy.svg"
import {
  type CapitalStandardSubProduct,
  STANDARD_CAPITAL_PRODUCT_DESCRIPTION,
  STANDARD_CAPITAL_PRODUCT_NAME,
} from "@/screens/loans/utils/LoanCapitalProducts"
import { SecondaryLightButton } from "@/components/buttons/CommonButtons"
import SubSelectedIcon from "@/assets/icons/capital-product-check-teal.svg"
import { useEditLoan } from "@/queries/loan"
import { loanUpdatingState } from "@/stores/updateLoan"
import { showErrorToast } from "@/components/toasts/AppToast"
import ToolLine from "@/components/util/ToolLine"
import { numberToCurrency } from "@/utils/NumberUtils"
import DownArrow from "@/assets/icons/stock-green-down.svg"
import { useLoanContext } from "@/screens/loans/components/LoanContext"
import { Vertical } from "@/components/standard/BFLayout"
import { useIsDesktop } from "@/hooks/useIsDesktop"

interface LoanProductCardCombinedProps {
  loanId: string
  cardData: { products: ProductType[] } // Combined products data (from merge)
  loanData: LoanApplicationType
}

const LoanProductCardCombined: React.FC<LoanProductCardCombinedProps> = ({
  loanId,
  cardData,
  loanData,
}) => {
  const isDesktop = useIsDesktop()
  const isQualified = cardData.products[0].isEligible
  const isSelectable = true

  // Calculate hideDownArrow based on cashToClose values
  const hideCTCDownArrow =
    (cardData.products[0].product === "standard_low_cash" &&
      cardData.products[1].product === "standard_low_interest" &&
      (cardData.products[0].cashToClose || 0) >
        (cardData.products[1].cashToClose || 0)) ||
    false

  const determinedSelectionState = (
    subProduct: CapitalStandardSubProduct
  ): boolean => {
    if (
      loanData.leverageDiscount === null ||
      loanData.loanProduct !== "standard"
    ) {
      return false
    }
    if (
      subProduct === "standard_low_cash" &&
      loanData.leverageDiscount === false
    ) {
      return true
    } else if (
      subProduct === "standard_low_interest" &&
      loanData.leverageDiscount === true
    ) {
      return true
    }
    return false
  }

  return (
    <View
      className={`grow w-full p-5 bg-grey border rounded border-light-grey overflow-hidden flex-col relative`}
    >
      <View className="absolute top-[38px] right-[22px]">
        <GoldTrophy height={75} />
      </View>
      <View className="flex-row pt-2">
        <H2
          className={`${isSelectable ? "text-midnight-blue" : "text-dark-grey"} mr-2 mb-1`}
        >
          {STANDARD_CAPITAL_PRODUCT_NAME}
        </H2>

        {isQualified && (
          <View
            style={{
              backgroundColor: "#2ADC83",
              marginVertical: 5,
              marginLeft: 1,
            }}
            className={`px-2 justify-center items-center`}
          >
            <H6 color={"default"}>ELIGIBLE</H6>
          </View>
        )}
      </View>
      <Body
        className={`text-midnight-blue ${Platform.OS === "web" && isDesktop ? "max-w-[500px]" : "max-w-[212px]"} ${!isDesktop && Platform.OS === "web" ? "max-w-[300px]" : ""} mb-5`}
      >
        {STANDARD_CAPITAL_PRODUCT_DESCRIPTION}
      </Body>
      <H5>Choose the terms that best suit your needs:</H5>
      <View className="flex-row mt-3 justify-between gap-[8px]">
        <LoanSubProductItem
          key={0}
          loanId={loanId}
          itemIndex={0}
          product={cardData.products[0]}
          isSelected={determinedSelectionState("standard_low_cash")}
          hideDownArrow={hideCTCDownArrow}
        />
        <LoanSubProductItem
          key={1}
          loanId={loanId}
          itemIndex={1}
          product={cardData.products[1]}
          isSelected={determinedSelectionState("standard_low_interest")}
        />
      </View>
    </View>
  )
}

export default LoanProductCardCombined

interface LoanSubProductItemProps {
  itemIndex: number
  product: ProductType
  isSelected: boolean
  loanId: string
  hideDownArrow?: boolean
}

const LoanSubProductItem: React.FC<LoanSubProductItemProps> = ({
  itemIndex,
  product,
  isSelected,
  loanId,
  hideDownArrow = false,
}) => {
  const [selected, setSelected] = useState<boolean>(isSelected)
  const editLoanMutation = useEditLoan()
  const [loading, setIsLoading] = useState(false)
  const c2cIsZero: boolean = product.cashToClose === 0

  useEffect(() => {
    setSelected(isSelected)
  }, [isSelected])

  const handleSelectToggle = () => {
    if (loading) {
      return
    }
    setIsLoading(true)
    loanUpdatingState.set(true)

    let leverageDiscountUpdate: boolean | null

    if (!selected) {
      leverageDiscountUpdate = product.product === "standard_low_interest"
    } else {
      leverageDiscountUpdate = null
    }
    editLoanMutation.mutate(
      {
        loanId,
        loanInput: {
          loanProduct: "standard",
          leverageDiscount: leverageDiscountUpdate,
        },
      },
      {
        onSuccess: () => {
          setIsLoading(false)
          loanUpdatingState.set(false)
        },
        onError: (error) => {
          setIsLoading(false)
          loanUpdatingState.set(false)
          setSelected(false)
          console.error("Error updating loan sub product:", error)
          const displayMessage =
            "An error occurred while updating the loan product. Please try again."
          showErrorToast({
            title: "Selection Error",
            message: displayMessage,
          })
        },
      }
    )
    setSelected(!selected)
  }

  const ltcAtCompletion = Math.round(
    (product.loanToCostAtCompletion || 0) * 100 || 0
  )
  const interestRate = product.interestRate
    ? product.interestRate * 100 || 0.0
    : 0
  const originationFee = product.originationFee
    ? product.originationFee * 100
    : 0

  const { cashToCloseDisplaysAsRange } = useLoanContext()

  return (
    <View
      className={`flex-1 rounded-lg border border-light-grey p-2`}
      key={itemIndex}
    >
      <H5 className="text-midnight-blue mb-2">
        {itemIndex === 0 ? "Option 1" : "Option 2"}
      </H5>

      <ToolLine />

      <Vertical className="mt-4">
        <CardDetailItem
          isSelectable={true}
          subtitle="LTC"
          value={ltcAtCompletion}
          unit="%"
        />
        <View className="mt-[4px]" />
        <CardDetailItem
          isSelectable={true}
          subtitle="Interest"
          value={interestRate.toFixed(2)}
          displaysArrow={product.product === "standard_low_interest"}
          unit="%"
        />
        <View className="mt-[4px]" />
        <CardDetailItem
          isSelectable={true}
          subtitle="Origination"
          value={originationFee.toFixed(2)}
          unit="%"
        />

        {!c2cIsZero && (
          <>
            <ToolLine className="my-2" />
            <Tiny className="text-midnight-blue text-[12px] my-1">
              Cash to Close
              {cashToCloseDisplaysAsRange ? "\nEstimate" : ""}
            </Tiny>
            <View className="flex-row items-center mb-3 min-h-5">
              {product.product === "standard_low_cash" && !hideDownArrow && (
                <DownArrow style={{ marginRight: 3 }} />
              )}

              {cashToCloseDisplaysAsRange ? (
                <H6 className="text-midnight-blue">
                  {numberToCurrency(product.cashToCloseRangeMin || 0)} -{" "}
                  {numberToCurrency(product.cashToCloseRangeMax || 0)}
                </H6>
              ) : (
                <H6 className="text-midnight-blue">
                  {numberToCurrency(product.cashToClose || 0)}
                </H6>
              )}
            </View>
          </>
        )}

        <View className="mt-2 w-full">
          <SecondaryLightButton
            selected={selected}
            size={"sm"}
            testID={`select-loan-subproduct-${itemIndex}`}
            title={selected ? "Selected" : "Select"}
            loading={loading}
            icon={SubSelectedIcon}
            onPress={handleSelectToggle}
          />
        </View>
      </Vertical>
    </View>
  )
}

interface CardDetailItemProps {
  subtitle: string
  value: number | string
  isSelectable: boolean
  unit: string
  displaysArrow?: boolean
}

const CardDetailItem: React.FC<CardDetailItemProps> = ({
  subtitle,
  value,
  isSelectable,
  unit,
  displaysArrow = false,
}) => {
  return (
    <View>
      <View className="flex-row justify-between min-w-20">
        <Tiny className="text-midnight-blue text-[12px] mb-1 items-start">
          {subtitle}
        </Tiny>
        <View className="flex-row items-start">
          {displaysArrow && (
            <DownArrow style={{ marginTop: 4, marginRight: 3 }} />
          )}
          <H6
            className={`${
              isSelectable ? "text-midnight-blue" : "text-dark-grey"
            }`}
          >
            {value}
            {unit}
          </H6>
        </View>
      </View>
    </View>
  )
}
